import React, { useState, useEffect } from 'react';
import opportunityService from '../../services/opportunityService';
import './OpportunitiesSummary.css';

const OpportunitiesSummary = ({ onViewAll, compact = false }) => {
  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState({ total_active: 0, by_type: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOpportunitiesSummary();
  }, []);

  const fetchOpportunitiesSummary = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching opportunities summary...');

      // Fetch both opportunities and stats
      const [opportunitiesResponse, statsResponse] = await Promise.all([
        opportunityService.getActiveOpportunities(),
        opportunityService.getOpportunityStats()
      ]);

      console.log('📊 Opportunities response:', opportunitiesResponse);
      console.log('📈 Stats response:', statsResponse);

      // Handle both response formats (with success wrapper or direct data)
      if (opportunitiesResponse.success && opportunitiesResponse.data) {
        // Response has success wrapper
        const topOpportunities = opportunitiesResponse.data.opportunities
          .sort((a, b) => parseFloat(b.confidence_score) - parseFloat(a.confidence_score))
          .slice(0, 3);
        console.log('🎯 Top opportunities:', topOpportunities);
        setOpportunities(topOpportunities);
      } else if (opportunitiesResponse.opportunities) {
        // Response is direct data (apiClient stripped wrapper)
        const topOpportunities = opportunitiesResponse.opportunities
          .sort((a, b) => parseFloat(b.confidence_score) - parseFloat(a.confidence_score))
          .slice(0, 3);
        console.log('🎯 Top opportunities (direct):', topOpportunities);
        setOpportunities(topOpportunities);
      } else {
        console.error('❌ Opportunities response not successful:', opportunitiesResponse);
      }

      // Handle both response formats for stats
      if (statsResponse.success && statsResponse.data) {
        // Response has success wrapper
        console.log('📊 Setting stats:', statsResponse.data);
        setStats(statsResponse.data);
      } else if (statsResponse.by_type || statsResponse.total_active) {
        // Response is direct data (apiClient stripped wrapper)
        console.log('📊 Setting stats (direct):', statsResponse);
        setStats(statsResponse);
      } else {
        console.error('❌ Stats response not successful:', statsResponse);
      }

    } catch (err) {
      console.error('💥 Error fetching opportunities summary:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getOpportunityIcon = (type) => {
    switch (type) {
      case 'volume_spike': return '🔥';
      case 'price_breakout': return '📈';
      case 'trend_reversal': return '🔄';
      case 'social_buzz': return '📢';
      default: return '⭐';
    }
  };

  const getOpportunityLabel = (type) => {
    switch (type) {
      case 'volume_spike': return 'Volume Spike';
      case 'price_breakout': return 'Price Breakout';
      case 'trend_reversal': return 'Trend Reversal';
      case 'social_buzz': return 'Social Buzz';
      default: return 'Opportunity';
    }
  };

  if (loading) {
    return (
      <div className={`opportunities-summary ${compact ? 'compact' : ''}`}>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <span>Scanning opportunities...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`opportunities-summary ${compact ? 'compact' : ''}`}>
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <span>Failed to load opportunities</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`opportunities-summary ${compact ? 'compact' : ''}`}>
      {/* Debug Info */}
      <div style={{ fontSize: '10px', color: '#666', marginBottom: '8px', border: '1px solid #ccc', padding: '4px', background: '#f9f9f9' }}>
        Debug: opportunities={opportunities.length}, stats.total_active={stats.total_active}, loading={loading}, error={error}
      </div>

      {/* Stats Overview */}
      <div className="opportunities-stats">
        <div className="stat-item">
          <span className="stat-value">{stats.total_active}</span>
          <span className="stat-label">Active</span>
        </div>
        {stats.by_type && stats.by_type.slice(0, 3).map(stat => (
          <div key={stat.opportunity_type} className="stat-item">
            <span className="stat-icon">{getOpportunityIcon(stat.opportunity_type)}</span>
            <span className="stat-value">{stat.count}</span>
          </div>
        ))}
      </div>

      {/* Top Opportunities */}
      {opportunities.length > 0 ? (
        <div className="top-opportunities">
          {opportunities.map((opportunity, index) => (
            <div key={opportunity.id || index} className="opportunity-item">
              <div className="opportunity-info">
                <span className="opportunity-icon">
                  {getOpportunityIcon(opportunity.opportunity_type)}
                </span>
                <div className="opportunity-details">
                  <span className="opportunity-symbol">
                    {opportunity.symbol || 'Unknown'}
                  </span>
                  <span className="opportunity-type">
                    {getOpportunityLabel(opportunity.opportunity_type)}
                  </span>
                </div>
              </div>
              <div className="opportunity-confidence">
                <span className="confidence-score">
                  {(parseFloat(opportunity.confidence_score) * 100).toFixed(0)}%
                </span>
                <div className="confidence-bar">
                  <div
                    className="confidence-fill"
                    style={{ width: `${parseFloat(opportunity.confidence_score) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-opportunities">
          <span className="no-opportunities-icon">🔍</span>
          <span>No opportunities found</span>
          <small>Check back later for new opportunities</small>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
            API Status: {loading ? 'Loading...' : error ? `Error: ${error}` : 'Loaded but empty'}
          </div>
        </div>
      )}

      {/* View All Button */}
      {onViewAll && (
        <button className="view-all-btn" onClick={onViewAll}>
          View All Opportunities →
        </button>
      )}
    </div>
  );
};

export default OpportunitiesSummary;